import { Workorder } from '@certhon/models';
import { fetchApi, Plangroup } from '@/app/api';
import { resolveUrl, verifyResponse } from '@/lib/api-utils';
import {
  ACTIVE_PROGRESS_FILTER,
  fetchWorkorders,
  updateWorkorder,
  EditableWorkorderFieldsDto,
} from '../workorders.api';
import { RouteResponse } from './dayplanning.types';

export type { Plangroup } from '@/app/api';

/**
 * Fetch workorders for a specific day for the current user
 */
export async function fetchDayWorkorders(
  userId: number,
  date: string, // ISO date: "2024-11-28"
): Promise<Workorder[]> {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + 1);
  const endDate = nextDate.toISOString().split('T')[0];

  return fetchWorkorders({
    assigned_user_id: `eq.${userId}`,
    progress: ACTIVE_PROGRESS_FILTER,
    execution_date: `gte.${date}`,
    and: `(execution_date.lt.${endDate})`,
    order: 'execution_date.asc',
  });
}

/**
 * Fetch workorders for a week-centric view:
 *   - workorders in [weekStart, weekStart + 1w) for the user, AND
 *   - any still-open (uncompleted) workorders in [weekStart - 4w, weekStart).
 *
 * Implemented as a single PostgREST query against the [rangeStart, rangeEnd)
 * range, combined with an OR filter selecting either current-week rows or
 * uncompleted rows.
 */
export async function fetchWeekWorkorders(
  userId: number,
  weekStart: string, // ISO date: Monday of selected week
): Promise<Workorder[]> {
  const start = new Date(weekStart);
  const rangeStart = new Date(start);
  rangeStart.setDate(rangeStart.getDate() - 28);
  const rangeEnd = new Date(start);
  rangeEnd.setDate(rangeEnd.getDate() + 7);

  const rangeStartIso = rangeStart.toISOString().split('T')[0];
  const rangeEndIso = rangeEnd.toISOString().split('T')[0];

  return fetchWorkorders({
    assigned_user_id: `eq.${userId}`,
    progress: ACTIVE_PROGRESS_FILTER,
    execution_date: `gte.${rangeStartIso}`,
    and: `(execution_date.lt.${rangeEndIso})`,
    or: `(execution_date.gte.${weekStart},completed.is.false)`,
    order: 'execution_date.asc',
  });
}

/**
 * Update day planning fields for a workorder
 */
export async function updateDayPlanningFields(
  recnum: number,
  fields: EditableWorkorderFieldsDto,
): Promise<Workorder> {
  return updateWorkorder(recnum, fields);
}

/**
 * Fetch workorders for all users on a specific day
 */
export async function fetchAllDayWorkorders(
  date: string,
): Promise<Map<number, Workorder[]>> {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + 1);
  const endDate = nextDate.toISOString().split('T')[0];

  const workorders = await fetchWorkorders({
    progress: ACTIVE_PROGRESS_FILTER,
    execution_date: `gte.${date}`,
    and: `(execution_date.lt.${endDate})`,
    order: 'execution_date.asc',
  });

  // Group workorders by assigned user
  const workordersByUser = new Map<number, Workorder[]>();
  for (const wo of workorders) {
    if (!wo.assigned_user_id) continue;

    if (!workordersByUser.has(wo.assigned_user_id)) {
      workordersByUser.set(wo.assigned_user_id, []);
    }
    workordersByUser.get(wo.assigned_user_id)!.push(wo);
  }

  return workordersByUser;
}

/**
 * Fetch routes between addresses
 * Returns array of RouteResponse objects (either successful RouteResult or RouteError with message)
 */
export async function fetchRoutes(
  routePairs: { from: string; to: string }[],
): Promise<RouteResponse[]> {
  if (routePairs.length === 0) return [];

  const routesParam = routePairs.map((p) => `${p.from},${p.to}`).join('|');

  const response = await fetch(
    resolveUrl('/routing', { routes: routesParam }),
    {
      credentials: 'include',
    },
  );

  await verifyResponse(response);
  return response.json();
}

/**
 * Fetch all plangroups with their member user IDs
 */
export async function fetchPlangroups(): Promise<Plangroup[]> {
  return fetchApi('/plangroups');
}

// =============================================================================
// Route Ordering Clarity API
// =============================================================================

/**
 * Response from route operations endpoint
 */
export interface RouteOperationResponse {
  routingKey: string;
  workorders: Array<{
    recnum: number;
    sort_order: number;
    planned_arrive_time: string | null;
    route_option: 'direct' | 'via_hq';
    sort_order_source: 'auto' | 'manual';
    routing_key: string;
    routing_timestamp: string;
  }>;
}

/**
 * Optimize (auto-sort) a route for a specific day/user
 * Calculates optimal route order using exhaustive permutation search
 */
export async function optimizeRoute(
  date: string,
  userId: number,
): Promise<RouteOperationResponse> {
  const dayAndUser = `${date}_${userId}`;

  const response = await fetch(resolveUrl(`/workorder_routes/${dayAndUser}`), {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ source: 'auto' }),
  });

  await verifyResponse(response);
  return response.json();
}

/**
 * Manually update route order for a specific day/user
 * Admin only - sets sort_order_source to 'manual'
 */
export async function updateRouteManual(
  date: string,
  userId: number,
  routeInfo: Array<{
    recnum: number;
    time: string | null;
    order: number;
    route_option: 'direct' | 'via_hq';
  }>,
): Promise<RouteOperationResponse> {
  const dayAndUser = `${date}_${userId}`;

  const response = await fetch(resolveUrl(`/workorder_routes/${dayAndUser}`), {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      source: 'manual',
      route_info: routeInfo,
    }),
  });

  await verifyResponse(response);
  return response.json();
}

/**
 * Fetch workorders for a month-centric view: everything scheduled in the
 * month plus unfinished carry-over from the four weeks before it.
 * `monthStart` must be the first day of the month (yyyy-MM-dd).
 */
export async function fetchMonthWorkorders(
  userId: number,
  monthStart: string,
): Promise<Workorder[]> {
  const start = new Date(monthStart);
  const rangeStart = new Date(start);
  rangeStart.setDate(rangeStart.getDate() - 28);
  const rangeEnd = new Date(start);
  rangeEnd.setMonth(rangeEnd.getMonth() + 1);

  const rangeStartIso = rangeStart.toISOString().split('T')[0];
  const rangeEndIso = rangeEnd.toISOString().split('T')[0];

  return fetchWorkorders({
    assigned_user_id: `eq.${userId}`,
    progress: ACTIVE_PROGRESS_FILTER,
    execution_date: `gte.${rangeStartIso}`,
    and: `(execution_date.lt.${rangeEndIso})`,
    or: `(execution_date.gte.${monthStart},completed.is.false)`,
    order: 'execution_date.asc',
  });
}
